import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { TYPE_FIELDS, ReservationField } from "./EditReservationDialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plane, Building2, Car, Ticket, ArrowLeft } from "lucide-react";

const TYPES = [
  { key: "flight", label: "Flight", Icon: Plane },
  { key: "hotel", label: "Hotel", Icon: Building2 },
  { key: "car", label: "Car rental", Icon: Car },
  { key: "event", label: "Event", Icon: Ticket },
];

export default function AddReservationDialog({ tripId, onClose, onAdded }) {
  const [type, setType] = useState(null);
  const [form, setForm] = useState({});
  const [estCost, setEstCost] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { type, [type]: form };
      const cost = parseFloat(estCost);
      if (!isNaN(cost) && cost >= 0) body.est_cost = cost;
      await api.post(`/trips/${tripId}/reservations`, body);
      toast.success(`${TYPES.find((t) => t.key === type).label} added`);
      onAdded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add item");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-sand-300" data-testid="add-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight flex items-center gap-3">
            {type && (
              <button onClick={() => { setType(null); setForm({}); }} className="text-sand-400 hover:text-ink" data-testid="add-back-btn" aria-label="Back to type selection">
                <ArrowLeft size={20} />
              </button>
            )}
            {type ? `Add ${TYPES.find((t) => t.key === type).label.toLowerCase()}` : "Add to trip"}
          </DialogTitle>
        </DialogHeader>

        {!type ? (
          <div className="grid grid-cols-2 gap-3 py-2">
            {TYPES.map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setType(key)}
                className="border border-sand-300 hover:border-ember hover:bg-ember/5 p-6 flex flex-col items-center gap-3 transition-colors"
                data-testid={`add-type-${key}`}>
                <Icon size={24} className="text-ember" />
                <span className="font-mono text-xs uppercase tracking-wide">{label}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[55vh] overflow-auto pr-2 py-2">
              {TYPE_FIELDS[type].map((f) => (
                <ReservationField
                  key={f[0]}
                  field={f}
                  value={form[f[0]]}
                  onChange={(v) => setForm({ ...form, [f[0]]: v })}
                  prefix="add"
                />
              ))}
              <div>
                <label className="label-tag block mb-1">Estimated cost ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  className="swiss-input text-sm"
                  value={estCost}
                  onChange={(e) => setEstCost(e.target.value)}
                  data-testid="add-est-cost"
                />
              </div>
            </div>
            <DialogFooter>
              <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="add-cancel">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-swiss btn-primary text-sm" data-testid="add-save">
                {busy ? "Adding…" : "Add to trip"}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
